import { LocaleAnchor, LocaleImage } from '@/i18n/router';
import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import { messages, type Messages } from '@/i18n/catalogs/editorial/upcoming-events';
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, ArrowRight, MapPin, Mic, Clock } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';

export interface UpcomingEvent {
  id: string;
  title: string;
  excerpt: string;
  startsAt: string; // ISO 8601 with timezone
  endsAt?: string;
  displayDate: string; // human-friendly, e.g. "Tue, May 12, 2026 · 2:00–3:00 PM"
  location: string;
  speaker?: string;
  registerUrl: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageStyle?: 'cover' | 'contain';
  imageBgClass?: string;
}

const getEvents = (m: Messages): UpcomingEvent[] => [
  {
    id: 'gizmosql-powerbi-webinar-2026-05-12',
    title: m.m000,
    excerpt:
      m.m001,
    startsAt: '2026-05-12T15:00:00-03:00',
    endsAt: '2026-05-12T16:00:00-03:00',
    displayDate: m.m002,
    location: m.m003,
    speaker: m.m004,
    registerUrl: 'https://www.linkedin.com/events/7451255077677428736',
    image: '/events/gizmosql-powerbi-webinar-2026-05-12.webp',
    imageWidth: 1280,
    imageHeight: 720,
  },
  {
    id: 'iot-tech-expo-north-america-2026-05-18',
    title: m.m005,
    excerpt:
      m.m006,
    startsAt: '2026-05-18T09:00:00-07:00',
    endsAt: '2026-05-19T17:00:00-07:00',
    displayDate: m.m007,
    location: m.m008,
    speaker: m.m009,
    registerUrl: 'https://www.iottechexpo.com/northamerica/',
    image: '/events/iot-tech-expo-north-america-2026.webp',
    imageWidth: 1359,
    imageHeight: 704,
    imageStyle: 'contain',
    imageBgClass: 'bg-gradient-to-br from-slate-900 to-slate-700',
  },
  {
    id: 'motherduck-duckdb-may-meetup-2026-05-21',
    title: m.m010,
    excerpt:
      m.m011,
    startsAt: '2026-05-21T18:00:00-07:00',
    endsAt: '2026-05-21T20:30:00-07:00',
    displayDate: m.m012,
    location: m.m013,
    speaker: m.m014,
    registerUrl: 'https://luma.com/motherduckdckdbmay',
    image: '/events/motherduck-duckdb-may-meetup-2026-05-21.webp',
    imageWidth: 1600,
    imageHeight: 1600,
    imageStyle: 'contain',
  },
];

export const upcomingEvents = getEvents(messages.en);

const getRelativeLabel = (startsAt: string, formatLocale: string): string | null => {
  const now = new Date();
  const start = new Date(startsAt);
  const diffMs = start.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return null;
  const relative = new Intl.RelativeTimeFormat(formatLocale, { numeric: 'auto' });
  if (diffDays === 0) return relative.format(0, 'day');
  if (diffDays === 1) return relative.format(1, 'day');
  if (diffDays <= 7) return relative.format(diffDays, 'day');
  if (diffDays <= 30) {
    const weeks = Math.round(diffDays / 7);
    return relative.format(weeks, 'week');
  }
  return null;
};

const UpcomingEventsSection = () => {
  const m = useMessages(messages);
  const upcomingEvents = getEvents(m);
  const { locale, formatLocale } = useLocale();
  // The relative label ("In 3 days"/"Tomorrow") depends on the current time, so it
  // can't be deterministic between SSG build and client hydration. Gate it behind
  // mount so the server HTML and first client render agree (both omit it).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Which events are past depends on the clock too: until hydration is done, judge it at
  // build time (the moment the prerendered HTML was made), then by the visitor's clock.
  const now = mounted ? new Date() : new Date(__BUILD_TIME__);
  const visibleEvents = upcomingEvents
    .filter((event) => {
      const end = new Date(event.endsAt ?? event.startsAt);
      return end.getTime() >= now.getTime();
    })
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  if (visibleEvents.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-gizmo-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-gizmo-600 text-white hover:bg-gizmo-600">
            <Calendar className="h-3 w-3 mr-1" />
            {m.m015}{' '}</Badge>
          <SectionHeading withId anchor="events" className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {m.m016}{' '}</SectionHeading>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {m.m017}{' '}</p>
        </div>

        <div
          className={`grid gap-8 ${
            visibleEvents.length === 1
              ? 'grid-cols-1 max-w-2xl mx-auto'
              : visibleEvents.length === 2
              ? 'grid-cols-1 md:grid-cols-2'
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          {visibleEvents.map((event) => {
            const relativeLabel = mounted ? getRelativeLabel(event.startsAt, formatLocale) : null;
            return (
              <div
                key={event.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl dark:hover:shadow-white/10 transition-shadow duration-300 border border-gizmo-100 dark:border-gray-700 overflow-hidden flex flex-col"
              >
                {event.image && (
                  <LocaleAnchor
                    href={event.registerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block aspect-video overflow-hidden ${event.imageBgClass ?? 'bg-gray-100 dark:bg-gray-700'}`}
                  >
                    <LocaleImage
                      src={event.image}
                      alt={event.title}
                      width={event.imageWidth}
                      height={event.imageHeight}
                      className={`w-full h-full hover:scale-105 transition-transform duration-300 ${
                        event.imageStyle === 'contain' ? 'object-contain p-2' : 'object-cover'
                      }`}
                      loading="lazy"
                    />
                  </LocaleAnchor>
                )}
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200 flex items-center gap-1">
                      <Mic className="h-3 w-3" />
                      {m.m018}{' '}</Badge>
                    {relativeLabel && (
                      <Badge
                        variant="outline"
                        className="border-gizmo-500 text-gizmo-700 dark:text-gizmo-300 flex items-center gap-1"
                      >
                        <Clock className="h-3 w-3" />
                        {relativeLabel}
                      </Badge>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {event.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-300 mb-4 flex-grow">
                    {event.excerpt}
                  </p>

                  <div className="space-y-2 mb-5 text-sm">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>{event.displayDate}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>{event.location}</span>
                    </div>
                    {event.speaker && (
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Mic className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>{event.speaker}</span>
                      </div>
                    )}
                  </div>

                  <LocaleAnchor
                    href={event.registerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-gizmo-600 hover:bg-gizmo-700 text-white group">
                      {m.m019}{' '}<ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </LocaleAnchor>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UpcomingEventsSection;
